import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useChatStore from 'chat-ui/src/store';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import Chat from '@/components/chat/Chat';
import ConsentScreen from '@/components/chat/ConsentScreen';
import { useGetScreeningQuestions, useThemeQuery } from '@/reactQuery/get';
import { ConversationStatus, ScreeningState } from '@/enums/enums';

function ChatPage() {
  const projectId = window.location.pathname;
  const isErrorScreen = projectId.includes('error');

  const [searchParams] = useSearchParams();
  const environment = searchParams.get('environment');
  const isSandbox = environment === ConversationStatus.SANDBOX;
  const interviewSource = searchParams.get('source');
  const skipConsent = searchParams.get('skipConsent') === 'true';
  const urlLanguage = searchParams.get('language');
  const inputMethodsParam = searchParams.get('inputMethods');

  // Parse and validate inputMethods URL parameter
  // Supports: text, audio, voice (alias for audio), video, image
  const parseInputMethods = (param: string | null): string[] | null => {
    if (!param) return null;
    const validMethods = ['text', 'audio', 'video', 'image'];
    const methodMap: Record<string, string> = { voice: 'audio' }; // friendly name mapping

    const parsed = param
      .split(',')
      .map((m) => m.trim().toLowerCase())
      .map((m) => methodMap[m] || m)
      .filter((m) => validMethods.includes(m));

    return parsed.length > 0 ? parsed : null;
  };

  const urlInputMethods = parseInputMethods(inputMethodsParam);
  const [projectConsent, setProjectConsent] = useState(false);
  const { setChatSettingsData } = useChatSettingsStore();
  const { setCurrentConversationId } = useChatStore((state) => state);
  const { data: themeData } = useThemeQuery(projectId, isErrorScreen);
  const interviewSourceSuffix = interviewSource ? `_${interviewSource}` : '';
  const sandboxSuffix = isSandbox ? `/${environment}` : '';
  const projectStorageSuffix = `${projectId}${interviewSourceSuffix}${sandboxSuffix}`;
  const areScreeningQuestionsOngoing = iframeLocalStorage.getItem(
    `screening_state${projectStorageSuffix}`,
  );
  const prescreenStatus = isSandbox ? ConversationStatus.SANDBOX : ConversationStatus.PUBLISHED;
  const shouldFetchScreeningQuestions =
    !!projectId &&
    areScreeningQuestionsOngoing &&
    areScreeningQuestionsOngoing !== ScreeningState.QUALIFIED;
  // Build metadata object from URL parameters
  const metadata = {
    ...(interviewSource && { source: interviewSource }),
    ...(urlLanguage && { language: urlLanguage }),
  };

  const { data: screeningQuestions, refetch } = useGetScreeningQuestions(
    projectId,
    !!shouldFetchScreeningQuestions,
    prescreenStatus,
    metadata,
  );

  useEffect(() => {
    const localStorageConsent = iframeLocalStorage.getItem(
      `project_consent${projectId}${isSandbox ? '/sandbox' : ''}${
        interviewSource ? `_${interviewSource}` : ''
      }`,
    );
    const localStorageConversationId = iframeLocalStorage.getItem(
      `current_conversation_id${projectStorageSuffix}`,
    );
    if (localStorageConversationId) {
      setCurrentConversationId(localStorageConversationId);
    }
    const chatSettings = iframeLocalStorage.getItem(`chat_settings${projectId}`);
    const chatSettingsFormatted = chatSettings?.split(',');
    setChatSettingsData(chatSettingsFormatted as string[]);
    setProjectConsent(localStorageConsent === 'true');
  }, [projectId, isSandbox, setChatSettingsData]);

  if (!projectConsent) {
    return (
      <ConsentScreen
        projectId={projectId}
        setProjectConsent={setProjectConsent}
        themeData={themeData}
        screeningQuestions={screeningQuestions}
        refetch={refetch}
        interviewSource={interviewSource}
        projectStorageSuffix={projectStorageSuffix}
        skipConsent={skipConsent}
        urlLanguage={urlLanguage}
        urlInputMethods={urlInputMethods}
      />
    );
  }

  return (
    <div className="min450:bg-white chat-page">
      <div
        style={{ backgroundColor: themeData?.['chat-background'] || '#F3F4F6' }}
        className="fixed top-0 bottom-0 left-0 right-0 w-full h-full min500:!bg-white"
      ></div>
      <Chat
        chatHeight="h-[calc(100dvh-95px)] scrollbar-hidden min500:h-[calc(100dvh-85px)]"
        projectId={projectId}
        setProjectConsent={setProjectConsent}
        screeningQuestions={screeningQuestions}
        projectStorageSuffix={projectStorageSuffix}
      />
    </div>
  );
}

export default ChatPage;
